import React, {
  Fragment,
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import clsx from 'clsx';
import {
  playerOverlayState,
  usePlayerOverlayStore,
} from '@app/web-player/state/player-overlay-store';
import {IconButton} from '@ui/buttons/icon-button';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {KeyboardArrowUpIcon} from '@ui/icons/material/KeyboardArrowUp';
import {MusicNoteIcon} from '@ui/icons/material/MusicNote';
import {PlaybackControls} from '@app/web-player/player-controls/playback-controls';
import {useLyrics} from '@app/web-player/tracks/lyrics/use-lyrics';
import {useCurrentTime} from '@common/player/hooks/use-current-time';
import {useCuedTrack} from '@app/web-player/player-controls/use-cued-track';
import {ArtistLinks} from '@app/web-player/artists/artist-links';
import {LikeIconButton} from '@app/web-player/library/like-icon-button';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {TrackContextDialog} from '@app/web-player/tracks/context-dialog/track-context-dialog';
import {MoreVertIcon} from '@ui/icons/material/MoreVert';
import fscreen from 'fscreen';
import {TrackTable} from '@app/web-player/tracks/track-table/track-table';
import {LyricsButton} from '@app/web-player/player-controls/lyrics-button';
import {useMediaQuery} from '@ui/utils/hooks/use-media-query';
import {DownloadTrackButton} from '@app/web-player/player-controls/download-track-button';
import {TrackLink} from '@app/web-player/tracks/track-link';
import {useLocation} from 'react-router-dom';
import {usePrevious} from '@ui/utils/hooks/use-previous';
import {PlayerOutlet} from '@common/player/ui/player-outlet';
import {PlayerPoster} from '@common/player/ui/controls/player-poster';
import {MediaFullscreenIcon} from '@ui/icons/media/media-fullscreen';
import {MediaQueueListIcon} from '@ui/icons/media/media-queue-list';
import {useMiniPlayerIsHidden} from '@app/web-player/overlay/use-mini-player-is-hidden';
import {usePlayerClickHandler} from '@common/player/hooks/use-player-click-handler';
import {QueueTrackContextDialog} from '@app/web-player/layout/queue/queue-track-context-dialog';
import {RowElementProps} from '@common/ui/tables/table-row';
import {Track} from '@app/web-player/tracks/track';
import {TableContext} from '@common/ui/tables/table-context';
import {MediaItem} from '@common/player/media-item';
import {TrackImage} from '@app/web-player/tracks/track-image/track-image';

import axios from 'axios';

export function PlayerOverlay() {
  // Usando um breakpoint maior (1200px) para capturar todos os iPads, incluindo os modelos Pro
  const isMobileOrTablet = useMediaQuery('(max-width: 1200px)');
  const isMaximized = usePlayerOverlayStore(s => s.isMaximized);
  const isQueueOpen = usePlayerOverlayStore(s => s.isQueueOpen);
  const isFullscreen = usePlayerStore(s => s.isFullscreen);
  const miniPlayerIsHidden = useMiniPlayerIsHidden();
  const overlayRef = useRef<HTMLDivElement>(null);
  const {pathname} = useLocation();
  const playerClickHandler = usePlayerClickHandler();
  const haveVideo = usePlayerStore(
    s => s.providerApi != null && s.providerName !== 'htmlAudio',
  );
  const previousPathname = usePrevious(pathname);
  const cuedTrack = useCuedTrack();

  // State for controlling the reserved area visibility
  const [isAreaVisible, setAreaVisible] = useState(false);
  const [startY, setStartY] = useState(0);
  const [isDragging, setDragging] = useState(false);
  

  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Definindo uma localização padrão em vez de solicitar a geolocalização do usuário
  useEffect(() => {
    // Usando uma localização padrão (exemplo: São Paulo)
    const defaultLatitude = -23.5505;
    const defaultLongitude = -46.6333;
    
    setLocation({
      lat: defaultLatitude,
      lng: defaultLongitude,
      address: "Brasil" // Endereço padrão
    });
    
    // Nota: A solicitação de geolocalização foi desativada conforme solicitado
  }, []);


  const toggleArea = () => {
    setAreaVisible(!isAreaVisible);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
    setDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevenir a atualização da página
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;
  
    // Inverter a lógica aqui
    if (deltaY < -50) {
      setAreaVisible(true); // Mostrar área ao arrastar para cima
    } else if (deltaY > 50) {
      setAreaVisible(false); // Esconder área ao arrastar para baixo
    }
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };

  // Manipuladores de mouse
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevenir a atualização da página
    setStartY(e.clientY);
    setDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevenir a atualização da página
    if (!isDragging) return;
    const currentY = e.clientY;
    const deltaY = currentY - startY;
  
    // Inverter a lógica aqui
    if (deltaY < -50) {
      setAreaVisible(true); // Mostrar área ao arrastar para cima
    } else if (deltaY > 50) {
      setAreaVisible(false); // Esconder área ao arrastar para baixo
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  // close overlay when route changes
  useEffect(() => {
    if (isMaximized && previousPathname && pathname !== previousPathname) {
      playerOverlayState.toggle();
    }
  }, [pathname, previousPathname, isMaximized]);

  useEffect(() => {
    if (!isMaximized) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        playerOverlayState.toggle();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMaximized]);



  // Nova opção listar



  return (
    <div
      ref={overlayRef}
      className={clsx(
        'fixed right-0 bg outline-none transition-all',
        miniPlayerIsHidden && !isMaximized && 'hidden',
        isMaximized
          ? 'player-overlay-bg bottom-0 flex h-full w-full flex-col pb-50'
          : 'bottom-96 right-0 h-[213px] w-256',
      )}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {isMaximized && (
        <div className="mb-10 flex flex-shrink-0 items-center p-10">
          <IconButton
            iconSize="lg"
            className="mr-auto"
            onClick={() => playerOverlayState.toggle()}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
          {isMobileOrTablet && <LyricsButton />}
          {isMobileOrTablet && <DownloadTrackButton />}
          <IconButton
            onClick={() => playerOverlayState.toggleQueue()}
            color={isQueueOpen ? 'primary' : undefined}
          >
            <MediaQueueListIcon />
          </IconButton>
          <FullscreenButton overlayRef={overlayRef} />
        </div>
      )}
      <div
        onClick={() => {
          if (!isFullscreen && !isMobileOrTablet) {
            playerClickHandler();
          }
        }}
        className={clsx(
          'relative min-h-0 max-w-full flex-auto',
          isMaximized ? 'mx-auto mt-auto px-14' : 'h-full w-full',
          isMaximized && haveVideo ? 'aspect-video' : 'aspect-square max-h-400',
        )}
      >
        {/* Camada de bloqueio principal para toda a área do player em dispositivos móveis e tablets */}
        {isMobileOrTablet && (
          <div 
            className="absolute inset-0" 
            style={{ 
              pointerEvents: 'all', 
              touchAction: 'none',
              zIndex: 45 // Entre a camada de cor (z-40) e o container de capa/letras (z-50)
            }}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onPointerMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
          />
        )}
        <div className={haveVideo ? 'h-full w-full flex-auto bg-black relative' : undefined}>
          {/* Player de vídeo do YouTube */}
          <div className="h-full w-full relative">
            <PlayerOutlet className="h-full w-full" />
            {/* Camada de bloqueio para impedir TODAS as interações com o iframe em TODOS os dispositivos */}
            <div 
              className="absolute inset-0 z-30" 
              style={{ 
                pointerEvents: 'all', 
                touchAction: 'none'
              }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onPointerMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
            />
          </div>
          
          {/* Desktop: Container para a capa */}
          <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ display: isMobileOrTablet ? 'none' : 'flex' }}>
            <div 
              className="aspect-square rounded-[30px] overflow-hidden shadow-lg"
              style={{
                width: 'calc(100% - 80px)', /* 40px de cada lado */
                maxHeight: '80%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {cuedTrack ? (
                <div className="w-full h-full relative overflow-hidden">
                  <TrackImage
                    className="absolute inset-0 w-full h-full object-cover"
                    background="bg-[#f5f5f5] dark:bg-[#2c2c35]"
                    track={cuedTrack}
                  />
                </div>
              ) : (
                <PlayerPoster className="h-full w-full" />
              )}
            </div>
          </div>
          
          {/* Camada de cor do tema por cima do iframe do YouTube para todos os dispositivos */}
          <div className="absolute inset-0 z-40 bg-paper dark:bg-alt" />
          
          {/* Mobile e Tablets: Container para capa e letras */}
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center" style={{ display: isMobileOrTablet ? 'flex' : 'none', pointerEvents: 'auto' }}>
            <div 
              className="mx-[20px] aspect-square rounded-[30px] overflow-hidden shadow-lg"
              style={{
                width: 'calc(100% - 40px)', /* 20px de cada lado */
                maxHeight: '60%', /* Reduzido para dar espaço às letras */
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* Usar diretamente a imagem da faixa, não o PlayerPoster */}
              {cuedTrack ? (
                <div className="w-full h-full relative overflow-hidden">
                  <TrackImage
                    className="absolute inset-0 w-full h-full object-cover"
                    background="bg-[#f5f5f5] dark:bg-[#2c2c35]"
                    track={cuedTrack}
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#f5f5f5] dark:bg-[#2c2c35]">
                  <MusicNoteIcon className="max-w-[60%] text-divider" size="text-9xl" />
                </div>
              )}
            </div>
            
            {/* Letras sincronizadas abaixo da capa */}
            {cuedTrack && (
              <div className="mt-[35px] w-full max-w-[calc(100%-40px)]">
                <SyncedLyricsPreview trackId={cuedTrack.id} />
              </div>
            )}
          </div>
        </div>
      </div>
      {isMaximized && (
        <Fragment>
          <QueuedTrack />
          <PlaybackControls className="container mx-auto mb-auto flex-shrink-0 px-14" />
        </Fragment>
      )}
      {isMaximized && isQueueOpen && <PlayerQueue />}
      {/* Reserved Area - Painel "Perto de Você" */}
      <div
        className={clsx(
          'fixed left-0 right-0 bottom-0 transition-transform duration-300',
          isAreaVisible ? 'translate-y-0' : 'translate-y-full'
        )}
        style={{ height: '580px', zIndex: 100 }} /* Aumentado o z-index para ficar por cima de tudo */
      >
        <div className="player-overlay-bg flex flex-col items-center justify-start h-full relative">
          {/* Botão de fechar no canto superior direito */}
          {isAreaVisible && (
          <button
            style={{
              position: 'absolute',
              top: '-50px',
              right: '20px',
              backgroundColor: 'rgb(0 0 0 / 49%)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              cursor: 'pointer',
            }}
            onClick={() => setAreaVisible(false)} // Fechar ao clicar
          >
            ✕
          </button>
        )}
  
          {/* Linhas no topo */}
          <div className="absolute top-0 mt-[7px] w-full flex flex-col items-center">
            <div className="w-[12%] h-[3px] bg-[#949597a1] mb-[3px]" /> {/* Primeira linha */}
            <div className="w-[12%] h-[3px] bg-[#949597a1]" /> {/* Segunda linha */}
          </div>
          <h2 className="mt-[30px]">Perto de Você</h2>
          {/* Exibe o endereço ou erro abaixo do título */}
          {/*location && <p>{location.address}</p>*/}
          {/*error && <p>{error}</p>*/}
          {/* Exibir o iframe aqui o Card em scroll list */}
          {/*location ? (
            <iframe
              src={`https://rda.brazucahub.com/display_zy.php?lat=${location.lat}&lng=${location.lng}`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="BrazucaAds Display"
            />
          ) : (
            <p>Carregando ofertas perto de você...</p>
          )*/}
        </div>
      </div>
    </div>
  );
  
}


interface FullscreenButtonProps {
  overlayRef: MutableRefObject<HTMLDivElement | null>;
}
// Componente para exibir 3 linhas de letras sincronizadas (anterior, atual e próxima)
interface SyncedLyricsPreviewProps {
  trackId: number;
}

function SyncedLyricsPreview({trackId}: SyncedLyricsPreviewProps) {
  const duration = usePlayerStore(s => s.mediaDuration);
  const {data} = useLyrics(trackId, {duration});
  const currentTime = useCurrentTime({precision: 'seconds'});
  
  // Se não houver letras ou se as letras não forem sincronizadas, não exibe nada
  if (!data || !data.is_synced || !data.lines.length) {
    return null;
  }
  
  // Verifica se as letras realmente têm timestamps
  const hasTimestamps = data.lines.some(line => line.time !== undefined);
  if (!hasTimestamps) {
    return null;
  }
  
  // Encontra o índice da linha atual com base no tempo de reprodução
  let currentIndex = 0;
  for (let i = 0; i < data.lines.length; i++) {
    const currentLineTime = data.lines[i].time || 0;
    const nextLine = data.lines[i+1];
    const nextLineTime = nextLine?.time || Infinity;
    
    if (currentLineTime <= currentTime && nextLineTime > currentTime) {
      currentIndex = i;
      break;
    }
  }
  
  // Obtém a linha anterior, atual e próxima
  const prevLine = currentIndex > 0 ? data.lines[currentIndex - 1] : null;
  const currentLine = data.lines[currentIndex];
  const nextLine = currentIndex < data.lines.length - 1 ? data.lines[currentIndex + 1] : null;
  
  return (
    <div className="w-full flex flex-col items-center text-center mt-4 px-4 space-y-2 text-white">
      {prevLine && (
        <div className="text-sm opacity-60 truncate w-full">
          {prevLine.text}
        </div>
      )}
      <div className="text-base font-semibold truncate w-full">
        {currentLine.text}
      </div>
      {nextLine && (
        <div className="text-sm opacity-60 truncate w-full">
          {nextLine.text}
        </div>
      )}
    </div>
  );
}

function FullscreenButton({overlayRef}: FullscreenButtonProps) {
  const playerReady = usePlayerStore(s => s.providerReady);
  return (
    <IconButton
      className={clsx(
        'ml-12 flex-shrink-0 max-md:hidden',
        !fscreen.fullscreenEnabled && 'hidden',
      )}
      disabled={!playerReady}
      onClick={() => {
        if (!overlayRef.current) return;
        if (fscreen.fullscreenElement) {
          fscreen.exitFullscreen();
        } else {
          fscreen.requestFullscreen(overlayRef.current);
        }
      }}
    >
      <MediaFullscreenIcon />
    </IconButton>
  );
}

function QueuedTrack() {
  const track = useCuedTrack();

  if (!track) {
    return null;
  }

  return (
    <div className="container mx-auto my-40 flex flex-shrink-0 items-center justify-center gap-34 px-14 md:my-60">
      <LikeIconButton likeable={track} />
      <div className="min-w-0 text-center">
        <div className="overflow-hidden overflow-ellipsis whitespace-nowrap text-base">
          <TrackLink track={track} />
        </div>
        <div className="text-sm text-muted">
          <ArtistLinks artists={track.artists} />
        </div>
      </div>
      <DialogTrigger type="popover" mobileType="tray">
        <IconButton>
          <MoreVertIcon />
        </IconButton>
        <TrackContextDialog tracks={[track]} />
      </DialogTrigger>
    </div>
  );
}

function PlayerQueue() {
  const queue = usePlayerStore(s => s.shuffledQueue);
  const tracks = queue.map(item => item.meta);
  return (
    <div className="fixed bottom-0 left-0 right-0 top-70 overflow-y-auto bg-inherit px-14 md:px-50">
      <TrackTable
        tracks={tracks}
        queueGroupId={queue[0]?.groupId}
        renderRowAs={PlayerQueueRow}
      />
    </div>
  );
}

function PlayerQueueRow({item, children, ...domProps}: RowElementProps<Track>) {
  const queue = usePlayerStore(s => s.shuffledQueue);
  const {selectedRows} = useContext(TableContext);
  const queueItems = useMemo(() => {
    return selectedRows
      .map(trackId => queue.find(item => item.meta.id === trackId))
      .filter(t => !!t) as MediaItem[];
  }, [queue, selectedRows]);

  const row = <div {...domProps}>{children}</div>;
  if (item.isPlaceholder) {
    return row;
  }

  return (
    <DialogTrigger
      type="popover"
      mobileType="tray"
      triggerOnContextMenu
      placement="bottom-start"
    >
      {row}
      <QueueTrackContextDialog queueItems={queueItems} />
    </DialogTrigger>
  );
}
